import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { CreateCourtesyDto } from "src/app/core/models/http/treatment/courtesies/create-courtesy-dto";
import { IEnvironmentConfig } from "src/app/shared/interfaces/IEnvironmentConfig";
import { map } from "rxjs/operators";
import {
  IMaxxOptimizedBenefitDto,
  IMaxxOptimizerPatchDto,
  IMaxxOptimizerQueryDto,
} from "@core/models/http/treatment/treatment-plans/optimizer-dto";
import { UpdateCourtesyDto } from "../models/http/treatment/courtesies/update-courtesy-dto";
import { CreateExpenseFeeDto } from "../models/http/treatment/fees/create-expense-fee-dto";
import { UpdateExpenseFeeDto } from "../models/http/treatment/fees/update-expense-fee-dto";
import { UpdateTreatmentFeeDto } from "../models/http/treatment/fees/update-treatment-fee-dto";
import { UpdateTreatmentOptionFeeDto } from "../models/http/treatment/fees/update-treatment-option-fee-dto";
import { UpdateInsurancePolicyBenefitDto } from "../models/http/treatment/insurance-policy-benefits/update-insurance-policy-benefit-dto";
import { TreatmentNotificationDto } from "../models/http/treatment/notifications/treatment-notification-dto";
import { SaveDiagnosisCodesDto } from "../models/http/treatment/patient-exam/save-diagnosis-codes-dto";
import { UpdatePatientExamDto } from "../models/http/treatment/patient-exam/update-patient-exam-dto";
import { TreatmentConfigurationDto } from "../models/http/treatment/treatment-configuration-dto";
import { TreatmentContractDto } from "../models/http/treatment/treatment-contract-dto";
import { TreatmentPlanBenefitDto } from "../models/http/treatment/treatment-plan-benefits/treatment-plan-benefit-dto";
import { UpdateTreatmentPlanBenefitDto } from "../models/http/treatment/treatment-plan-benefits/update-treatment-plan-benefit-dto";
import {
  AddOptimizedTreatmentPlanDto,
  AddTreatmentPlanDto,
} from "../models/http/treatment/treatment-plans/add-treatment-plan-dto";
import { TreatmentPlanDto } from "../models/http/treatment/treatment-plans/treatment-plan-dto";
import { UpdateAcceledentDto } from "../models/http/treatment/treatment-plans/update-acceledent-dto";
import { UpdateTreatmentLengthDto } from "../models/http/treatment/treatment-plans/update-treatment-length-dto";
import { UpdateLineItemInsuranceBenefitDto } from "../models/http/treatment/insurance-policy-benefits/line-items/update-line-item-insurance-benefit-dto";
import { UpdateLineItemPatientCopayDto } from "../models/http/treatment/insurance-policy-benefits/line-items/update-line-item-patient-copay-dto";
import { ValidationErrorDto } from "../models/http/validation-error-dto";

/** Credentials to be used when interacting with OrthoFi APIs */
const httpOptions = { withCredentials: true };

@Injectable({
  providedIn: "root",
})
export class TreatmentPlanService {
  private readonly _contractRoot: string;
  private readonly _treatmentPlanRoot: string;

  constructor(
    @Inject("ENV") private _env: IEnvironmentConfig,
    private _http: HttpClient
  ) {
    this._treatmentPlanRoot = `${_env.gatekeeperUrl}/api/v1/treatment-contract`;
    this._contractRoot = `${_env.gatekeeperUrl}/api/v1/treatment-contract`;
  }

  public calculateBenefitsByTreatmentPlanId(
    treatmentPlanId: number
  ): Observable<TreatmentPlanBenefitDto> {
    return this._http.post<TreatmentPlanBenefitDto>(
      `${this._treatmentPlanRoot}/treatment-plan/${treatmentPlanId}/benefits/calculate`,
      {},
      httpOptions
    );
  }

  public calculateOptimizedBenefitsByTreatmentPlanId(
    treatmentPlanId: number
  ): Observable<IMaxxOptimizedBenefitDto> {
    return this._http.post<IMaxxOptimizedBenefitDto>(
      `${this._treatmentPlanRoot}/treatment-plan/${treatmentPlanId}/benefits/optimized-calculate`,
      {},
      httpOptions
    );
  }

  public calculateInsuranceBenefit(
    contractId: number
  ): Observable<TreatmentPlanBenefitDto[]> {
    return this._http.post<TreatmentPlanBenefitDto[]>(
      `${this._treatmentPlanRoot}/${contractId}/benefits`,
      {},
      httpOptions
    );
  }

  public createCourtesy(command: CreateCourtesyDto): Observable<void> {
    return this._http.post<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/courtesy/${command.Id}`,
      command,
      httpOptions
    );
  }

  public createExpenseFee(command: CreateExpenseFeeDto): Observable<void> {
    return this._http.post<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/expense-fee/${command.Id}`,
      command,
      httpOptions
    );
  }

  public createTreatmentPlan(
    requestBody: AddTreatmentPlanDto
  ): Observable<TreatmentPlanDto> {
    return this._http.post<TreatmentPlanDto>(
      `${this._treatmentPlanRoot}/${requestBody.ContractId}/treatment-plan`,
      requestBody,
      httpOptions
    );
  }

  public createTreatmentPlanOptimizer(
    requestBody: AddOptimizedTreatmentPlanDto
  ): Observable<IMaxxOptimizerQueryDto> {
    return this._http.post<IMaxxOptimizerQueryDto>(
      `${this._treatmentPlanRoot}/${requestBody.ContractId}/optimizer`,
      requestBody,
      httpOptions
    );
  }

  public duplicateTreatmentPlan(
    treatmentPlanId: number
  ): Observable<TreatmentPlanDto> {
    return this._http.post<TreatmentPlanDto>(
      `${this._treatmentPlanRoot}/treatment-plan/${treatmentPlanId}/duplicate`,
      {},
      httpOptions
    );
  }

  public getInsuranceBenefit(
    contractId: number
  ): Observable<TreatmentPlanBenefitDto[]> {
    return this._http.get<TreatmentPlanBenefitDto[]>(
      `${this._treatmentPlanRoot}/${contractId}/benefits`,
      httpOptions
    );
  }

  public getInsuranceTimestamp(contractId: number): Observable<Date> {
    return this._http
      .get<{ Timestamp: string }>(
        `${this._treatmentPlanRoot}/${contractId}/insurance-timestamp`,
        httpOptions
      )
      .pipe(map((t) => new Date(t.Timestamp + "Z")));
  }

  public getNotificationMessagesByContractId(
    contractId: number
  ): Observable<TreatmentNotificationDto[]> {
    return this._http.get<TreatmentNotificationDto[]>(
      `${this._treatmentPlanRoot}/${contractId}/notifications`,
      httpOptions
    );
  }

  public getNotificationMessagesByTreatmentPlanId(
    treatmentPlanId: number
  ): Observable<TreatmentNotificationDto[]> {
    return this._http.get<TreatmentNotificationDto[]>(
      `${this._treatmentPlanRoot}/treatment-plan/${treatmentPlanId}/notifications`,
      httpOptions
    );
  }

  public getTreatmentConfiguration(
    contractId: number
  ): Observable<TreatmentConfigurationDto> {
    return this._http.get<TreatmentConfigurationDto>(
      `${this._contractRoot}/${contractId}/treatment-configuration`,
      httpOptions
    );
  }

  public getTreatmentPlans(
    contractId: number
  ): Observable<TreatmentContractDto> {
    return this._http.get<TreatmentContractDto>(
      `${this._treatmentPlanRoot}/${contractId}/treatment-plans`,
      httpOptions
    );
  }

  public overrideBenefit(
    command: UpdateInsurancePolicyBenefitDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/benefit/${command.InsurancePolicyId}/override`,
      command,
      httpOptions
    );
  }

  public removeCourtesy(
    treatmentPlanId: number,
    courtesyId: string
  ): Observable<void> {
    return this._http.delete<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${treatmentPlanId}/courtesy/${courtesyId}`,
      httpOptions
    );
  }

  public removeExpense(
    treatmentPlanId: number,
    feeId: string
  ): Observable<void> {
    return this._http.delete<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${treatmentPlanId}/expense-fee/${feeId}`,
      httpOptions
    );
  }

  public removeTreatmentPlan(id: number): Observable<void> {
    return this._http.delete<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${id}`,
      httpOptions
    );
  }

  public resetOptimizedTreatmentPlansByContractId(contractId: number): Observable<void> {
    return this._http.post<void>(
      `${this._treatmentPlanRoot}/${contractId}/contract-optimizer-reset`,
      {},
      httpOptions
    )
  }

  public updateAcceledentOption(
    command: UpdateAcceledentDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/acceledent`,
      command,
      httpOptions
    );
  }

  public updateCourtesy(command: UpdateCourtesyDto): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/courtesy/${command.Id}`,
      command,
      httpOptions
    );
  }

  public updateDiagnosisCodes(
    command: SaveDiagnosisCodesDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/${command.ContractId}/diagnosis-codes`,
      command,
      httpOptions
    );
  }

  public updateExamDetails(
    requestBody: UpdatePatientExamDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._contractRoot}/${requestBody.ContractId}/patient-exam`,
      requestBody,
      httpOptions
    );
  }

  public updateExpenseFee(command: UpdateExpenseFeeDto): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/expense-fee/${command.Id}`,
      command,
      httpOptions
    );
  }

  public updateLineItemInsuranceBenefit(
    command: UpdateLineItemInsuranceBenefitDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/line-item/${command.LineItemId}/insurance-benefit`,
      command,
      httpOptions
    );
  }

  public updateLineItemPatientCopay(
    command: UpdateLineItemPatientCopayDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/line-item/${command.LineItemId}/patient-copay`,
      command,
      httpOptions
    );
  }

  public updateTreatmentFee(command: UpdateTreatmentFeeDto): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/treatment-fee/${command.Id}`,
      command,
      httpOptions
    );
  }

  public updateTreatmentLength(
    command: UpdateTreatmentLengthDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/treatment-length`,
      command,
      httpOptions
    );
  }

  public updateTreatmentOptionFee(
    command: UpdateTreatmentOptionFeeDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/treatment-option-fee/${command.Id}`,
      command,
      httpOptions
    );
  }

  public updateTreatmentPlanBenefit(
    command: UpdateTreatmentPlanBenefitDto
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/treatment-plan-benefit/${command.TreatmentPlanBenefitId}`,
      command,
      httpOptions
    );
  }

  public updateTreatmentPlanName(
    treatmentPlanId: number,
    name: string
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._treatmentPlanRoot}/treatment-plan/${treatmentPlanId}/name`,
      {
        Name: name,
        TreatmentPlanId: treatmentPlanId,
      },
      httpOptions
    );
  }

  public updateOptimizer(
    command: IMaxxOptimizerPatchDto
  ): Observable<IMaxxOptimizerQueryDto> {
    return this._http.patch<IMaxxOptimizerQueryDto>(
      `${this._treatmentPlanRoot}/treatment-plan/${command.TreatmentPlanId}/optimizer`,
      command,
      httpOptions
    );
  }

  /**
   * Check for any issues with the contract and treatment plans (E.g. treatment
   * lengths are not less than 0, deactivated codes aren't in use, etc),
   * automatically attempting to fix any of the issues found.
   *
   * @prop contractId the contract that will be validated and modified as needed.
   * @returns A list of problems that we unable to be resolved
   */
  public validateAndCorrectContract(
    contractId: number
  ): Observable<ValidationErrorDto[]> {
    return this._http.post<ValidationErrorDto[]>(
      `${this._treatmentPlanRoot}/${contractId}/validate`,
      null,
      httpOptions
    );
  }
}
